/**
 * KarroFin Admin — display formatting helpers.
 *
 * Indian numbering throughout (lakh/crore grouping), matching the legacy
 * panel's use of toLocaleString("en-IN") in pages/pl-funnel/index.vue.
 */

/** 1234567 -> "12,34,567" */
export function number(value) {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (isNaN(n)) return "-";
  return n.toLocaleString("en-IN");
}

/** 1234567 -> "₹12,34,567" */
export function currency(value) {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (isNaN(n)) return "-";
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

/**
 * Compact currency for KPI tiles: 12345678 -> "₹1.23 Cr".
 * Full precision stays available in the tile's tooltip.
 */
export function currencyShort(value) {
  const n = Number(value);
  if (isNaN(n) || value === null || value === undefined) return "-";
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000) return "₹" + (n / 100000).toFixed(2) + " L";
  if (n >= 1000) return "₹" + (n / 1000).toFixed(1) + "K";
  return "₹" + n.toLocaleString("en-IN");
}

/** 12345 -> "12.3K" */
export function numberShort(value) {
  const n = Number(value);
  if (isNaN(n) || value === null || value === undefined) return "-";
  if (n >= 10000000) return (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000) return (n / 100000).toFixed(2) + " L";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n);
}

/** "2026-08-12" -> "12-08-2026", mirroring the legacy display_date_formate. */
export function date(value) {
  if (!value) return "-";
  // Split on a space as well as a "T". Everything that reaches this from the
  // karrofin api layer has been through isoDate() and is "YYYY-MM-DDTHH:MM",
  // but the legacy endpoints hand back "YYYY-MM-DD HH:MM:SS" -- and on those
  // the time used to survive into the middle of the output as
  // "24 19:16:02-08-2026". An ISO string contains no space, so this is strictly
  // more permissive.
  const raw = String(value).split(/[T ]/)[0];
  const parts = raw.split("-");
  if (parts.length !== 3) return raw;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A date range short enough to fit a filter control: "20 May - 18 Aug 2026",
 * or "20 - 28 Aug 2026" when both ends share a month.
 *
 * The long form ("20-05-2026 -> 18-08-2026") is 23 characters and was being
 * clipped by every box it was put in.
 */
export function dateRangeShort(from, to) {
  const part = (v) => {
    const raw = String(v || "").split("T")[0].split("-");
    if (raw.length !== 3) return null;
    return { y: raw[0], m: Number(raw[1]) - 1, d: String(Number(raw[2])) };
  };
  const a = part(from);
  const b = part(to);
  if (!a || !b) return "All dates";
  if (a.y === b.y && a.m === b.m) return `${a.d} - ${b.d} ${MONTHS[b.m]} ${b.y}`;
  if (a.y === b.y) return `${a.d} ${MONTHS[a.m]} - ${b.d} ${MONTHS[b.m]} ${b.y}`;
  return `${a.d} ${MONTHS[a.m]} ${a.y} - ${b.d} ${MONTHS[b.m]} ${b.y}`;
}

/** "2026-08-12T09:30:00" -> "12-08-2026, 09:30 AM" */
export function dateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return date(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  let hh = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${dd}-${mm}-${yyyy}, ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
}

/**
 * "2026-08-12T04:00:05Z" -> "12-08-2026, 09:30:05 AM IST"
 *
 * Always Asia/Kolkata, whatever the browser's zone -- for audit screens where
 * the exact moment matters and two people comparing notes must read the same
 * time. dateTime() above follows the browser and drops the seconds.
 */
const IST_PARTS = (() => {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return null;
  }
})();

export function dateTimeIst(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime()) || !IST_PARTS) return dateTime(value);
  const p = {};
  IST_PARTS.formatToParts(d).forEach((part) => {
    p[part.type] = part.value;
  });
  const period = (p.dayPeriod || "").toUpperCase();
  return `${p.day}-${p.month}-${p.year}, ${p.hour}:${p.minute}:${p.second} ${period} IST`;
}

/** "2026-08-01" -> "11 days ago" */
export function relative(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** 0.4213 -> "42.1%" */
export function percent(value, digits = 1) {
  const n = Number(value);
  if (isNaN(n)) return "-";
  return (n * 100).toFixed(digits) + "%";
}

/** Safe ratio that renders "-" instead of NaN/Infinity on a zero denominator. */
export function rate(numerator, denominator, digits = 1) {
  if (!denominator) return "-";
  return percent(numerator / denominator, digits);
}

/** "9876543210" -> "98765 43210" */
export function mobile(value) {
  if (!value) return "-";
  const s = String(value).replace(/\D/g, "").slice(-10);
  if (s.length !== 10) return String(value);
  return s.slice(0, 5) + " " + s.slice(5);
}

/** Initials for an avatar chip: "Anita Desai" -> "AD" */
export function initials(name) {
  if (!name) return "?";
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

/** Blank-safe passthrough so tables never render "null". */
export function orDash(value) {
  if (value === null || value === undefined || value === "") return "-";
  return value;
}

export default {
  number,
  currency,
  currencyShort,
  numberShort,
  date,
  dateRangeShort,
  dateTime,
  dateTimeIst,
  relative,
  percent,
  rate,
  mobile,
  initials,
  orDash,
};
